import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import StatusPill from "../../components/StatusPill";
import AdminNav from "../../components/AdminNav";
import { CommercialChip } from "../../components/CommercialChip";
import { fmtDT, fmtINR, nextAction, isExceptionState } from "../../lib/constants";
import { HandledHistoryPanel } from "../../components/HandledHistoryPanel";
import { toast } from "sonner";
import { MagnifyingGlass, Package as PackageIcon } from "@phosphor-icons/react";

export default function AdminDashboard() {
  const { profile } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [view, setView] = useState("active");  // B6 — CTOP: 'active' | 'history'

  useEffect(() => {
    api.get("/shipments").then((r) => setShipments(r.data)).catch((e) => toast.error(e.displayMessage)).finally(() => setLoading(false));
  }, []);

  if (!profile) return null;
  if (profile.role === "SENDER") return <Navigate to="/sender" replace />;
  if (profile.role === "CAP")
    return <Navigate to="/captain" replace />;

  const filtered = shipments.filter((s) => {
    if (statusFilter && s.current_status !== statusFilter) return false;
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      s.public_shipment_number.toLowerCase().includes(f) ||
      s.route_code.toLowerCase().includes(f)
    );
  });

  const statuses = Array.from(new Set(shipments.map((s) => s.current_status))).sort();

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="overline">CONTROL TOWER</div>
          <h1 className="heading text-3xl">Shipments</h1>
        </div>
        <div className="flex items-center gap-3">
          <AdminNav />
        </div>
      </div>

      {profile.role === "CTOP" && (
        <div className="flex gap-2 mb-4" data-testid="ctop-view-tabs">
          <button
            className={view === "active" ? "btn-primary" : "btn-ghost"}
            onClick={() => setView("active")}
            data-testid="ctop-tab-active"
          >
            Active Shipments
          </button>
          <button
            className={view === "history" ? "btn-primary" : "btn-ghost"}
            onClick={() => setView("history")}
            data-testid="ctop-tab-history"
          >
            Handled History
          </button>
        </div>
      )}

      {view === "history" && profile.role === "CTOP" ? (
        <HandledHistoryPanel testidPrefix="ctop-handled-history" />
      ) : (
      <>
      <div className="grid-card p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="text-[hsl(var(--muted))]" />
          <input
            type="text"
            placeholder="Search by shipment number or route…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input-field"
            data-testid="shipment-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-auto"
          data-testid="status-filter-select"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="overline ml-auto" data-testid="shipment-count">{filtered.length} of {shipments.length}</span>
      </div>

      <div className="grid-card grid-card-strong">
        {loading ? (
          <div className="p-10 mono text-sm">Loading shipments…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <PackageIcon size={32} weight="duotone" className="mx-auto mb-3 opacity-50" />
            <div className="overline mb-1">NO SHIPMENTS</div>
            <p className="text-sm text-[hsl(var(--muted))]">No shipments match your filters.</p>
          </div>
        ) : (
          <table className="table-air12">
            <thead>
              <tr>
                <th>Shipment Number</th>
                <th>Route</th>
                <th>Status</th>
                <th>Commercial</th>
                <th>Next action</th>
                <th>Chargeable Wt</th>
                <th>Total Payable</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`shipment-row-${s.public_shipment_number}`}>
                  <td>
                    <Link to={`/admin/shipments/${s.id}`} className="id-chip id-chip-strong">{s.public_shipment_number}</Link>
                    {isExceptionState(s.current_status) && (
                      <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[hsl(var(--error)/0.12)] text-[hsl(var(--error))]" data-testid={`exception-badge-${s.public_shipment_number}`}>
                        EXCEPTION
                      </span>
                    )}
                  </td>
                  <td><span className="id-chip">{s.route_code}</span></td>
                  <td><StatusPill status={s.current_status} /></td>
                  <td><CommercialChip shipment={s} role={profile.role} /></td>
                  <td className="text-xs" data-testid={`next-action-${s.public_shipment_number}`}>
                    {s.refund_pending_action ? (
                      <Link to={`/admin/shipments/${s.id}`} className="underline font-semibold text-[hsl(var(--danger))]" data-testid={`refund-action-${s.public_shipment_number}`}>
                        Process manual refund
                      </Link>
                    ) : nextAction(s.current_status)}
                  </td>
                  <td className="mono">{s.total_chargeable_weight_kg} kg</td>
                  <td className="mono">{fmtINR(s.total_payable)}</td>
                  <td className="mono text-xs text-[hsl(var(--muted))]">{fmtDT(s.created_at)}</td>
                  <td>
                    <Link to={`/admin/shipments/${s.id}`} className="text-xs underline font-semibold" data-testid={`view-${s.public_shipment_number}`}>
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
}
